import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { RibbonButtonProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { ControlInfo, getControlInfo } from 'owa-mail-ribbon-utils';
import getFlagCompleteState, {
    FlagCompleteEnum,
} from 'owa-mail-triage-action/lib/actions/helpers/getFlagCompleteState';
import { MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import { onFlagUnflagClicked } from '../../util/commandActions';
import {
    createMailAppButton,
    createMailAppSelectionButton,
} from '../../util/createMailRibbonButton';
import { getFlagUnflagEnableMap, getSelectedFlagState } from '../../util/flagUnflagUtils';
import type { ClientItemId } from 'owa-client-ids';
import { getReadRibbonId } from '../../util/getReadRibbonId';

/**
 * This function returns the drop-down content of the "Flag / Unflag" button in the Ribbon.
 * There are two sections here:
 *      1) Due Dates (when the task is meant to complete, such as 'Today' or 'Next week', and so on)
 *      2) Triaging (section for actions like 'Mark complete', 'Clear flag', and so on)
 *
 * @param keytipTabId is the tab that this sections is being called from. Important for keytips.
 */
export default function getFlagUnflagSections(
    keytipTabId?: MailRibbonTabId,
    itemId?: ClientItemId
): MenuSection[] {
    const selectedFlagState: FlagCompleteEnum = getSelectedFlagState();

    return [
        { controls: getDueDateSection(selectedFlagState, keytipTabId, itemId) },
        { controls: getTriageSection(selectedFlagState, keytipTabId, itemId) },
    ];
}

function getDueDateSection(
    selectedFlagState: FlagCompleteEnum,
    keytipTabId?: MailRibbonTabId,
    itemId?: ClientItemId
): RibbonButtonProps[] {
    const flagNextWeekControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.FlagCompleteNextWeek
    );
    const flagNoDateControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.FlagCompleteNoDate
    );
    const flagThisWeekControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.FlagCompleteThisWeek
    );
    const flagTodayControlInfo: ControlInfo = getControlInfo(MailRibbonControlId.FlagCompleteToday);
    const flagTomorrowControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.FlagCompleteTomorrow
    );

    const keytipPrefixSequence: string[] = keytipTabId
        ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.FlagUnflag)]
        : [getKeytipString(MailRibbonControlId.FlagUnflag)];

    return [
        createMailAppSelectionButton(
            getReadRibbonId(MailRibbonControlId.FlagCompleteToday, itemId),
            flagTodayControlInfo.text,
            flagTodayControlInfo.tooltip,
            onFlagUnflagClicked(
                getFlagCompleteState(FlagCompleteEnum.today),
                true /* override */,
                itemId
            ),
            selectedFlagState === FlagCompleteEnum.today,
            constructKeytip(
                keytipPrefixSequence,
                getKeytipString(MailRibbonControlId.FlagCompleteToday)
            )
        ),
        createMailAppSelectionButton(
            getReadRibbonId(MailRibbonControlId.FlagCompleteTomorrow, itemId),
            flagTomorrowControlInfo.text,
            flagTomorrowControlInfo.tooltip,
            onFlagUnflagClicked(
                getFlagCompleteState(FlagCompleteEnum.tomorrow),
                true /* override */,
                itemId
            ),
            selectedFlagState === FlagCompleteEnum.tomorrow,
            constructKeytip(
                keytipPrefixSequence,
                getKeytipString(MailRibbonControlId.FlagCompleteTomorrow)
            )
        ),
        createMailAppSelectionButton(
            getReadRibbonId(MailRibbonControlId.FlagCompleteThisWeek, itemId),
            flagThisWeekControlInfo.text,
            flagThisWeekControlInfo.tooltip,
            onFlagUnflagClicked(
                getFlagCompleteState(FlagCompleteEnum.thisWeek),
                true /* override */,
                itemId
            ),
            selectedFlagState === FlagCompleteEnum.thisWeek,
            constructKeytip(
                keytipPrefixSequence,
                getKeytipString(MailRibbonControlId.FlagCompleteThisWeek)
            )
        ),
        createMailAppSelectionButton(
            getReadRibbonId(MailRibbonControlId.FlagCompleteNextWeek, itemId),
            flagNextWeekControlInfo.text,
            flagNextWeekControlInfo.tooltip,
            onFlagUnflagClicked(
                getFlagCompleteState(FlagCompleteEnum.nextWeek),
                true /* override */,
                itemId
            ),
            selectedFlagState === FlagCompleteEnum.nextWeek,
            constructKeytip(
                keytipPrefixSequence,
                getKeytipString(MailRibbonControlId.FlagCompleteNextWeek)
            )
        ),
        createMailAppSelectionButton(
            getReadRibbonId(MailRibbonControlId.FlagCompleteNoDate, itemId),
            flagNoDateControlInfo.text,
            flagNoDateControlInfo.tooltip,
            onFlagUnflagClicked(
                getFlagCompleteState(FlagCompleteEnum.noDate),
                true /* override */,
                itemId
            ),
            selectedFlagState === FlagCompleteEnum.noDate,
            constructKeytip(
                keytipPrefixSequence,
                getKeytipString(MailRibbonControlId.FlagCompleteNoDate)
            )
        ),
    ];
}

function getTriageSection(
    selectedFlagState: FlagCompleteEnum,
    keytipTabId?: MailRibbonTabId,
    itemId?: ClientItemId
): RibbonButtonProps[] {
    // Determine whether to enable actions in this section depending on
    // the current selected items' FlagType state.
    let enableMap: {
        shouldEnableMarkComplete: boolean;
        shouldEnableClearFlag: boolean;
    } = getFlagUnflagEnableMap();

    const clearFlagControlInfo: ControlInfo = getControlInfo(MailRibbonControlId.FlagClearFlag);
    const markCompleteControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.FlagMarkComplete
    );
    const keytipPrefixSequence: string[] = keytipTabId
        ? [getKeytipString(keytipTabId), getKeytipString(MailRibbonControlId.FlagUnflag)]
        : [getKeytipString(MailRibbonControlId.FlagUnflag)];

    return [
        createMailAppSelectionButton(
            getReadRibbonId(MailRibbonControlId.FlagMarkComplete, itemId),
            markCompleteControlInfo.text,
            markCompleteControlInfo.tooltip,
            onFlagUnflagClicked(getFlagCompleteState(FlagCompleteEnum.complete), undefined, itemId),
            selectedFlagState === FlagCompleteEnum.complete,
            constructKeytip(
                keytipPrefixSequence,
                getKeytipString(MailRibbonControlId.FlagMarkComplete)
            ),
            {
                disabled: enableMap.shouldEnableMarkComplete === false,
            }
        ),
        // Explicitly no selected state for Clear Flag button
        createMailAppButton(
            getReadRibbonId(MailRibbonControlId.FlagClearFlag, itemId),
            clearFlagControlInfo.text,
            clearFlagControlInfo.tooltip,
            false /*isPopout*/,
            onFlagUnflagClicked(getFlagCompleteState(FlagCompleteEnum.clear), undefined, itemId),
            constructKeytip(
                keytipPrefixSequence,
                getKeytipString(MailRibbonControlId.FlagClearFlag)
            ),
            clearFlagControlInfo.iconName,
            undefined /* iconColor */,
            undefined /* styles */,
            {
                disabled: enableMap.shouldEnableClearFlag === false,
            }
        ),
    ];
}
